/**
 * Ryan O'Dowd
 * 2022-04-17
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Dimensions,
  View,
} from 'react-native';
import {
  PRACTICE_ROUTE,
  repairStreakWithGrace,
} from '@utils/store/actions';
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  add as dateFnsAdd,
  format as dateFnsFormat,
} from 'date-fns';
import CPassage from '@classes/Passage';
import Icon from '@react-native-vector-icons/material-design-icons';
import Popover from '@components/common/Popover';
import Text from '@components/common/Text';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';

const dateOnlyFormat = 'yyyy-MM-dd';
const titleDateFormat = 'eeee, MMMM d';

const CELL_MARGIN = 2; // @TODO: remove need for this

const ContributionGraph = (_p: ContributionGraphProps) => {
  const props = {
    ...defaultProps,
    ..._p,
  };

  const dispatch = hooks.useTypedDispatch();

  // @TODO: const GRAPH_WIDTH = props.isHorizontal ? '100%' : Dimensions.get('window').width - 40; // @TODO: dynamic margin/padding subtraction (this is only good on profile)
  const GRAPH_WIDTH = Math.min(15 * 36, Dimensions.get('window').width - (props.isHorizontal ? 110 : 40)); // @TODO: dynamic margin/padding subtraction (this is only good on profile) // @TODO: this is fine on iphone se, but probably small on others // @TODO: replace with styles.windowWidth
  const CELL_DIMENSION = props.isHorizontal ? Math.floor(GRAPH_WIDTH / props.daysToDisplay - (2 * CELL_MARGIN)) : GRAPH_WIDTH / (props.daysToDisplay / 7) - (2 * CELL_MARGIN);

  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles, {graphWidth: GRAPH_WIDTH, cellDimension: CELL_DIMENSION}); // @TODO: shouldn't have to pass sizes in
  const i18n = hooks.useI18n();

  const [_earlyDatesInData, setEarlyDatesInData] = useState(false);

  const [_isRepairInProgressInGraph, setIsRepairInProgressInGraph] = useState(false);
  const [_isRepairableInGraph, setIsRepairableInGraph] = useState(false);
  const [_isFullyRepairedInGraph, setIsFullyRepairedInGraph] = useState(false);
  const [_isGraceRepairedInGraph, setIsGraceRepairedInGraph] = useState(false);

  const activeGoalId = hooks.useTypedSelector((state) => state.settingsActiveGoalId);

  const numDaysStreakGraceRemaining = hooks.useTypedSelector((state: any) => Math.max(0, state.streaks.num_grace_days_remaining));

  const _getMonthStyles = useCallback((date) => { // @NOTE: date should already have offset for user's tz
    if (props.isHorizontal) {
      return {};
    }

    let monthStyles = {};
    if (date.getDate() === 1 && date.getDay() !== 0) {
      monthStyles = {...monthStyles, ...styles.monthBoundaryTopBorder};
    }

    if (date.getDate() <= 7) {
      monthStyles = {...monthStyles, ...styles.monthBoundaryLeftBorder};
    }

    return monthStyles;
  }, [styles, props.isHorizontal]);

  const activePassages = hooks.useTypedSelector((state) => {
    // @TODO: activeGoal.passages.filter((p) => (peanuts[p.id] || 0) > 0).map((p) => CPassage.createFromObject(p));
    const passages = [];
    (state.goals[activeGoalId] || Object.values(state.goals)[0]).modules.forEach((module) => { // @TODO: this is hacky...shouldn't need to look through goals to get passages...should it be its own reducer?
      module.assignments.forEach((assignment) => {
        if (assignment.assignment_object.passage) { // @TODO: could also only get passages that have been worked on...if no peanuts/lessons for an assignment, skip it (though make sure that if goal hasn't been started, we should return at least one passage still)
          passages.push(CPassage.createFromObject(assignment.assignment_object.passage));
        }
      });
    });

    return passages;
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const _renderCell = useCallback((streakInfo) => { // @TODO: should be its own component
    const streakDateUserTz = new Date(streakInfo.date);
    const streakDateFormatted = dateFnsFormat(streakDateUserTz, dateOnlyFormat, {locale: i18n.dateFnsLocale}); // this is user's TZ (returned from back end, but this is stored in user's TZ there)
    const todayDateFormatted = dateFnsFormat(new Date(), dateOnlyFormat, {locale: i18n.dateFnsLocale}); // this is user's TZ
    const joinDate = new Date(props.user.created_at);
    const joinDateFormatted = dateFnsFormat(joinDate, dateOnlyFormat, {locale: i18n.dateFnsLocale}); // this is user's TZ
    /* @TODO:
    console.log({streakDateUserTz});
    console.log({todayDateFormatted});
    console.log({streakDateFormatted});
    */
    const isTodayCell = streakDateFormatted === todayDateFormatted;
    const isSelectedDate = streakDateFormatted === props.selectedDate;

    let otherStyles = styles.cellMissedAndFuture;
    let wrapperStyles = {};
    if (streakInfo.count > 0) {
      otherStyles = styles.cellCompleted;
    } else if (streakDateFormatted < joinDateFormatted) {
      otherStyles = styles.cellBeforeJoinDate;
      if (!_earlyDatesInData) {
        setEarlyDatesInData(true);
      }
    } else if (streakInfo.repairable && streakInfo.repair_peanuts_remaining !== null && streakInfo.repair_peanuts_remaining < globals.peanutsNeededToRepairStreak && streakInfo.repair_peanuts_remaining > 0) {
      // this is a partially repaired streak day
      otherStyles = styles.cellRepairInProgress;
    } else if (streakInfo.repair_peanuts_remaining === 0) {
      // this is a fully repaired streak day
      otherStyles = styles.cellFullyRepaired;
    } else if (streakInfo.repairable && !isTodayCell) {
      otherStyles = styles.cellRepairable;
    }

    if (isTodayCell) {
      otherStyles = {...otherStyles, ...styles.cellToday};
    }

    if (isSelectedDate || (props.isHorizontal && isTodayCell && !props.selectedDate)) {
      wrapperStyles = {...wrapperStyles, ...styles.cellSelected};
    }

    return (
      <Popover.Wrapper
        key={streakDateUserTz.getTime()}
        disabled={streakDateFormatted < joinDateFormatted}
        actions={[
          ((streakInfo.repairable === true && !isTodayCell) && {
            text: i18n('repairNow'),
            onPress: () => {
              // @TODO: navigation should be able to determine if navigate/replace is needed without me needing to pass prop
              if (props.practiceIsMounted) {
                navigation.replace(PRACTICE_ROUTE, {
                  passages: activePassages,
                  repairStreakDate: dateFnsFormat(streakDateUserTz, 'yyyy-MM-dd', {locale: i18n.dateFnsLocale}),
                });
              } else {
                navigation.navigate(PRACTICE_ROUTE, {
                  passages: activePassages,
                  repairStreakDate: dateFnsFormat(streakDateUserTz, 'yyyy-MM-dd', {locale: i18n.dateFnsLocale}),
                });
              }
            },
          }),
          (((streakInfo.repairable === false) && !isTodayCell && (numDaysStreakGraceRemaining > 0) && (streakInfo.repair_peanuts_remaining !== -1) && (streakDateFormatted < todayDateFormatted)) && {
            text: 'Repair with grace', // @TODO: i18n
            onPress: () => dispatch(repairStreakWithGrace(dateFnsFormat(streakDateUserTz, 'yyyy-MM-dd', {locale: i18n.dateFnsLocale}))),
          }),
        ].filter(Boolean)}
        popover={
          <Popover.Inner>
            <Popover.Text title={true}>{dateFnsFormat(streakDateUserTz, titleDateFormat, {locale: i18n.dateFnsLocale})}</Popover.Text>
            {/* @TODO: add graphic here */}
            {streakInfo.repairable === false && (
              streakInfo.repair_peanuts_remaining === -1
                ? <Popover.Text>This missed day was repaired by a grace day.</Popover.Text>
                : (
                  <React.Fragment>
                    <Popover.Text>Missed days are repairable for {globals.numDaysStreakIsRepairable} days.{/* @TODO: also say something more encouraging here */}</Popover.Text>{/* @TODO: looks like it might actually be 9 days? *//* @TODO: i18n */}
                    <Popover.Text>{`You currently have ${numDaysStreakGraceRemaining} grace ${numDaysStreakGraceRemaining === 1 ? 'day' : 'days'} remaining.`}</Popover.Text>{/* @TODO: i18n */}
                  </React.Fragment>
                )
            )}
            {streakInfo.repairable === null && streakInfo.repair_peanuts_remaining === null && (
              <Popover.Text>Congrats keeping your streak alive!{/* @TODO: maybe show other encouraging text too, so it's not always the same thing */}</Popover.Text>/* @TODO: i18n */
            )}
            {streakInfo.repairable === null && streakInfo.repair_peanuts_remaining === 0 && (
              <Popover.Text>Way to repair your streak!{/* @TODO: maybe show other encouraging text too, so it's not always the same thing */}</Popover.Text>/* @TODO: i18n */
            )}
            {(streakInfo.repairable === true && !isTodayCell) && (
              <View>
                <Popover.Text>{`Looks like you missed a day on ${dateFnsFormat(streakDateUserTz, 'M/d', {locale: i18n.dateFnsLocale})}.  But that's okay--you can still keep your streak alive by earning some extra peanuts!`}</Popover.Text>{/* @TODO: i18n */}
                <Popover.Text style={styles.repairPeanutsRemainingText}>{streakInfo.repair_peanuts_remaining} {streakInfo.repair_peanuts_remaining === 1 ? 'peanut' : 'peanuts'} remaining</Popover.Text>{/* @TODO: i18n */}
              </View>
            )}
            {streakDateFormatted > todayDateFormatted && (
              <Popover.Text>{`Come back and earn peanuts on ${dateFnsFormat(streakDateUserTz, 'eeee', {locale: i18n.dateFnsLocale})} to continue your streak!`}</Popover.Text>/* @TODO: i18n */
            )}
            {/* @TODO: add back and forward arrows to toggle days without having to close dialog...need this now that we have popovers instead of dialogs? */}
          </Popover.Inner>
        }
      >
        <View style={[_getMonthStyles(streakDateUserTz), wrapperStyles]}>
          <View
            style={[
              styles.cell,
              otherStyles,
            ]}
          >
            {streakInfo.repair_peanuts_remaining === 0 && (
              <Icon style={styles.cellIcon} name='shield-cross' color={styles.colors.accentDark} />
            )}
            {streakInfo.repair_peanuts_remaining === -1 && (
              <Icon style={styles.cellIcon} name='shield-cross' color={styles.colors.accent} />
            )}
          </View>
        </View>
      </Popover.Wrapper>
    );
  }, [dispatch, numDaysStreakGraceRemaining, props.user.created_at, _earlyDatesInData, activePassages, i18n, _getMonthStyles, styles, props.isHorizontal, props.selectedDate, props.practiceIsMounted, navigation]);

  const streaks = hooks.useTypedSelector((state: any) => {
    const streaksInfo = {...state.streaks};
    let streakEndDate = new Date(state.streaks.current_streak_end_date || 0); // this is in user's TZ
    streakEndDate = new Date(
      streakEndDate.getUTCFullYear(),
      streakEndDate.getUTCMonth(),
      streakEndDate.getUTCDate(),
      0, 0, 0,
    );
    const twoDaysAgoDate = dateFnsAdd(new Date(), {days: -2}); // @TODO: probably shouldn't be 2 days...could be as short as 23 hours and 59 minutes...
    if (streakEndDate <= twoDaysAgoDate) {
      // the "current streak" ended at least 2 days ago
      streaksInfo.current_streak_num = 0;
    }

    return streaksInfo;
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const streakChartData = useMemo(() => {
    let isRepairableInGraph = false;
    let isRepairInProgressInGraph = false;
    let isFullyRepairedInGraph = false;
    let isGraceRepairedInGraph = false;
    const retData = [...streaks.data].sort((a, b) => {
      return (new Date(a.date).valueOf()) - (new Date(b.date).valueOf());
    }).slice(Math.max(0, streaks.data.length - props.daysToDisplay)).map((streak) => {
      // @TODO: depending on the total numbers, i think there's an opacity issue where the colors can be wrong
      let count = 1;
      if (streak.repairable === false) {
        // skipped and not repairable
        count = 0;
        if (streak.repair_peanuts_remaining === -1) {
          // grace repaired
          isGraceRepairedInGraph = true;
        }
      } else if (streak.repair_peanuts_remaining === 0) {
        // fully repaired
        isFullyRepairedInGraph = true;
      } else if (streak.repair_peanuts_remaining !== null && streak.repair_peanuts_remaining < globals.peanutsNeededToRepairStreak && streak.repair_peanuts_remaining > 0) {
        // currently repairing
        count = 0;
        isRepairInProgressInGraph = true;
      } else if (streak.repairable && streak.repair_peanuts_remaining > 0) {
        // repairable but not started yet
        count = 0;
        if (dateFnsFormat(dateFnsAdd(new Date(streak.date), {hours: 12}), 'yyyy-MM-dd') < dateFnsFormat(new Date(), 'yyyy-MM-dd')) { // @TODO: adding 12 hours is hacky for tz issues right now
          // if these dates are equal, then it's past midnight in UTC but not in this user's timezone yet, so just check if repairable date is strictly less than today
          isRepairableInGraph = true;
        }
      }

      const foo = new Date(streak.date);
      const localMidnight = new Date(
        foo.getUTCFullYear(),
        foo.getUTCMonth(),
        foo.getUTCDate(),
        0, 0, 0,
      );

      return {
        ...streak,
        date: localMidnight,
        count,
      };
    });

    setIsRepairableInGraph(isRepairableInGraph);
    setIsRepairInProgressInGraph(isRepairInProgressInGraph);
    setIsFullyRepairedInGraph(isFullyRepairedInGraph);
    setIsGraceRepairedInGraph(isGraceRepairedInGraph);

    return retData;
  }, [streaks.data, props.daysToDisplay]);

  const streakData = useMemo(() => {
    const retVal = [...streakChartData];

    if (!props.isHorizontal) { // @TODO: better way to test this
      // pad future dates
      if (props.padRestOfWeek) {
        const latestDateFormatted = dateFnsFormat(new Date(retVal[retVal.length - 1]?.date || new Date()), dateOnlyFormat, {locale: i18n.dateFnsLocale});
        const todayDateFormatted = dateFnsFormat(new Date(), dateOnlyFormat, {locale: i18n.dateFnsLocale});
        const todayIsCompleted = streakChartData.length && latestDateFormatted === todayDateFormatted;
        [...Array((7 - (new Date()).getDay() - (todayIsCompleted ? 1 : 0)) || 7)].forEach((__, i) => {
          retVal.push({date: dateFnsAdd(new Date(), {days: i + (todayIsCompleted ? 1 : 0)})});
        });
      }

      // pad past dates (before sign up) if required
      const prePadNum = props.daysToDisplay - retVal.length;
      if (prePadNum > 0) {
        [...Array(prePadNum)].forEach((__, i) => {
          retVal.unshift({date: dateFnsAdd(new Date(streakChartData[0]?.date || new Date()), {days: -1 * (i + 1)})});
        });
      }
    }

    return retVal;
  }, [streakChartData, i18n, props.daysToDisplay, props.padRestOfWeek, props.isHorizontal]);

  const _renderMonthLabels = () => { // @TODO: would like to align these labels with the borders eventually, but this is fine for now
    const today = new Date(); // this is user's tz
    const firstOfThisMonth = new Date(today.setDate(1));

    return (
      <View style={styles.monthsLabels}>
        {[...new Array(4)].map((__, monthOffset) => {
          return (
            <Text
              key={monthOffset}
              style={styles.monthLabel}
            >
              {dateFnsFormat(firstOfThisMonth.setMonth((firstOfThisMonth.getMonth() - (monthOffset ? 1 : 0) + 12) % 12), 'MMMM', {locale: i18n.dateFnsLocale})}
            </Text>
          );
        }).reverse()}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={[styles.graph, props.isHorizontal && styles.horizontalGraph]}>
        {streakData.slice(Math.max(streakData.length - props.daysToDisplay, 0)).map((s) => {
          return _renderCell(s);
        })}
      </View>
      <View style={styles.graphLabels}>
        {!props.isHorizontal && _renderMonthLabels()}
      </View>
      <View style={styles.streakLegend}>
        <View style={styles.legendItemWrapper}>
          <View style={[styles.cell, styles.legendSquare, styles.cellCompleted, props.isHorizontal && styles.legendCellSmall]} />
          <Text style={styles.legendText}>{i18n('completed')}</Text>
        </View>
        {_isRepairableInGraph &&
          <View style={styles.legendItemWrapper}>
            <View style={[styles.cell, styles.legendSquare, styles.cellRepairable, props.isHorizontal && styles.legendCellSmall]} />
            <Text style={styles.legendText}>{i18n('repairable')}</Text>
          </View>
        }
        {_isFullyRepairedInGraph &&
          <View style={styles.legendItemWrapper}>
            <View style={[styles.cell, styles.legendSquare, styles.cellFullyRepaired, props.isHorizontal && styles.legendCellSmall]}>
              <Icon style={[styles.cellIcon, props.isHorizontal && styles.cellIconSmall]} name='shield-cross' color={styles.colors.accentDark} />
            </View>
            <Text style={styles.legendText}>{i18n('fullyRepaired')}</Text>
          </View>
        }
        {_isGraceRepairedInGraph &&
          <View style={styles.legendItemWrapper}>
            <View style={[styles.cell, styles.legendSquare, styles.cellMissedAndFuture, props.isHorizontal && styles.legendCellSmall]}>
              <Icon style={[styles.cellIcon, props.isHorizontal && styles.cellIconSmall]} name='shield-cross' color={styles.colors.accent} />
            </View>
            <Text style={styles.legendText}>{i18n('graceRepaired')}</Text>
          </View>
        }
        {_isRepairInProgressInGraph &&
          <View style={styles.legendItemWrapper}>
            <View style={[styles.cell, styles.legendSquare, styles.cellRepairInProgress, props.isHorizontal && styles.legendCellSmall]} />
            <Text style={styles.legendText}>{i18n('repairInProgress')}</Text>
          </View>
        }
        {_earlyDatesInData &&
          <View style={styles.legendItemWrapper}>
            <View style={[styles.cell, styles.legendSquare, styles.cellBeforeJoinDate]} />
            <Text style={styles.legendText}>{i18n('beforeJoinDate')}</Text>
          </View>
        }
      </View>
    </View>
  );
};

class ContributionGraphProps {
  user: any;
  padRestOfWeek: boolean;

  isHorizontal?: boolean;
  selectedDate?: string; // @TODO: should this be date object
  daysToDisplay?: number = 15 * 7;
  practiceIsMounted?: boolean = false;
};
const defaultProps = new ContributionGraphProps();

export default ContributionGraph;
